import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import classNames from "classnames";
import CloseIcon from "@/components/ui/OpticksIcons/CloseIcon";
import type { ModalOptions } from "@/types/model/Modal";
import "./OpticksModal.css";
import OpticksButton from "../OpticksButton/OpticksButton";
import OpticksTitleViewSection from "../OpticksTitleViewSection/OpticksTitleViewSection";

interface ModalContextTypes {
  openModal: (options: ModalOptions) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextTypes>({
  openModal: () => {},
  closeModal: () => {},
});

export const useModal = () => useContext(ModalContext);

// `:popover-open` throws a SyntaxError in browsers with partial Popover API
// support (older Safari/WebKit). Guard the match so the error never escapes;
// if we can't detect the state, assume it's open and run the close path — the
// `hidePopover()` call inside is already wrapped in a try/catch.
export const isPopoverOpen = (el: HTMLElement) => {
  try {
    return el.matches(":popover-open");
  } catch {
    return true;
  }
};

interface Props {
  children: React.ReactNode;
}

export const ModalProvider = ({ children }: Props) => {
  const [modal, setModal] = useState<ModalOptions | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openId, setOpenId] = useState(0);
  const popoverRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Latest options + open state, so closeModal stays stable and still fires
  // onClose exactly once per close.
  const modalRef = useRef<ModalOptions | null>(null);
  modalRef.current = modal;
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;

  const openModal = useCallback((options: ModalOptions) => {
    setModal(options);
    setOpenId((id) => id + 1);
    setIsOpen(true);
    isOpenRef.current = true;
  }, []);

  const closeModal = useCallback(() => {
    if (!isOpenRef.current) return;
    isOpenRef.current = false;
    setIsOpen(false);
    modalRef.current?.onClose?.();
  }, []);

  const contextValue = useMemo<ModalContextTypes>(
    () => ({ openModal, closeModal }),
    [openModal, closeModal]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeModal]);

  useEffect(() => {
    const popover = popoverRef.current;
    if (!popover) return undefined;
    if (isOpen) {
      popover.showPopover();
    } else if (isPopoverOpen(popover)) {
      popover.classList.add("is-closing");
      const onEnd = () => {
        popover.classList.remove("is-closing");
        try {
          popover.hidePopover();
        } catch {}
      };
      popover.addEventListener("transitionend", onEnd, { once: true });
      const fallback = setTimeout(onEnd, 350);
      return () => {
        clearTimeout(fallback);
        popover.removeEventListener("transitionend", onEnd);
        popover.classList.remove("is-closing");
      };
    }
    return undefined;
  }, [isOpen]);

  const handleClickOutside = (event: React.MouseEvent) => {
    if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
      closeModal();
    }
  };

  return (
    <ModalContext.Provider value={contextValue}>
      <div
        ref={popoverRef}
        id="opticks-modal"
        className="opticks-modal"
        popover="manual"
        onMouseDown={handleClickOutside}
      >
        <div
          className={classNames(
            "opticks-modal__body",
            modal?.bare ? "bare" : `size-${modal?.size ?? "m"}`
          )}
          ref={boxRef}
        >
          {modal?.bare ? (
            <OpticksButton
              aria-label="Close"
              className="opticks-modal__close-floating"
              icon={<CloseIcon size={24} className="icon-white" />}
              onClick={closeModal}
            />
          ) : (
            <div
              className={classNames("opticks-modal__header", {
                untitled: !modal?.title,
              })}
            >
              <div className="opticks-modal__title">
                {modal?.title && (
                  <OpticksTitleViewSection
                    type="section"
                    title={modal.title}
                    icon={modal.icon}
                  />
                )}
              </div>
              <OpticksButton
                aria-label="Close"
                icon={<CloseIcon size={24} />}
                onClick={closeModal}
              />
            </div>
          )}
          <div
            key={openId}
            className={classNames(
              "opticks-modal__content-wrapper",
              modal?.bare ? "bare" : `bg-${modal?.bg ?? "background"}`
            )}
          >
            {modal?.content}
          </div>
        </div>
      </div>
      {children}
    </ModalContext.Provider>
  );
};
